import type { Id, Page } from './common';

// Document ingestion, "Luồng bóc" (S5 spec §7, §8, §12, §14): a job reads a document, a set of images or a pasted
// text into a batch of proposed rows the person approves. The enums below equal the 024 CHECKs
// (tests/unit/ingest-contracts.test.ts reads the migration text).

export const INGEST_JOB_STATES = ['queued', 'reading', 'proposing', 'ready', 'applied', 'discarded', 'expired', 'failed',
  'cancelled'] as const;
export type IngestJobState = (typeof INGEST_JOB_STATES)[number];
export const INGEST_SOURCE_KINDS = ['file', 'images', 'text'] as const;
export type IngestSourceKind = (typeof INGEST_SOURCE_KINDS)[number];
/** What a batch row writes (§5.1); `metric_value` is a metric's value, stored as `metric` in `record_sources`. */
export const BATCH_ROW_KINDS = ['task', 'note', 'partner', 'contact', 'stakeholder', 'participant', 'contract',
  'finance_entry', 'metric_value', 'review'] as const;
export type BatchRowKind = (typeof BATCH_ROW_KINDS)[number];
export const BATCH_ROW_OPS = ['create', 'update'] as const;
export type BatchRowOp = (typeof BATCH_ROW_OPS)[number];
export const BATCH_ROW_STATUSES = ['ok', 'needs_fix', 'duplicate', 'rejected'] as const;
export type BatchRowStatus = (typeof BATCH_ROW_STATUSES)[number];

/** §11.4: jobs reading at once per person, jobs a day per person ★, rows a batch, PDF pages, images a job. The
 *  20 MB a file is the upload's own `MAX_OBJECT_BYTES`. */
export const INGEST_LIMITS = { busy: 2, daily: 30, rows: 500, pages: 100, images: 10 } as const;
/** A pasted text's length after `\p{Cf}` is stripped (§5.1, D54); the 1 500 trigger is the client's (L3). */
export const INGEST_TEXT_MAX = 100_000;

/** §14's codes. `ASSISTANT_QUOTA` is S1's and keeps S1's sentence. */
export const INGEST_ERROR_CODES = ['INGEST_DISABLED', 'INGEST_BUSY', 'INGEST_DAILY_LIMIT', 'INGEST_TOO_LARGE',
  'INGEST_UNSUPPORTED', 'INGEST_SCAN_UNSUPPORTED', 'INGEST_MODEL_ERROR', 'INGEST_EMPTY', 'INGEST_STALLED',
  'BATCH_CHANGED', 'BATCH_EXPIRED', 'INGEST_RANGE_TAKEN'] as const;
export type IngestErrorCode = (typeof INGEST_ERROR_CODES)[number];
/** The failures "Thử lại" offers (§14, P23). */
export const INGEST_RETRYABLE = ['ASSISTANT_QUOTA', 'INGEST_MODEL_ERROR', 'INGEST_STALLED'] as const;
export type IngestRetryableCode = (typeof INGEST_RETRYABLE)[number];

/**
 * The sentence of each code, once (§14). `{address}` is how Hana addresses the person (`ingestMessage`). The
 * too-large, unsupported and scan sentences have specific forms below; these are their fallbacks.
 */
export const INGEST_MESSAGES: Readonly<Record<IngestErrorCode, string>> = {
  INGEST_DISABLED: 'Admin đã tắt bóc tài liệu',
  INGEST_BUSY: `Em đang đọc ${INGEST_LIMITS.busy} tài liệu của {address}, chờ xong một cái nhé`,
  INGEST_DAILY_LIMIT: `Hôm nay {address} đã gửi ${INGEST_LIMITS.daily} tài liệu`,
  INGEST_TOO_LARGE: 'Tài liệu vượt giới hạn trợ lý đọc được một lần',
  INGEST_UNSUPPORTED: 'Em không đọc được loại file này',
  INGEST_SCAN_UNSUPPORTED: 'Có trang là ảnh scan, model hiện tại không đọc được',
  INGEST_MODEL_ERROR: 'Em đọc phần này bị lỗi',
  INGEST_EMPTY: 'Em không thấy dữ liệu nào cần ghi trong tài liệu này',
  INGEST_STALLED: 'Em bị gián đoạn khi đọc',
  BATCH_CHANGED: 'Dữ liệu đã thay đổi, xem lại các dòng được đánh dấu rồi duyệt lại',
  BATCH_EXPIRED: 'Lô này đã hết hạn',
  INGEST_RANGE_TAKEN: 'Khoảng dòng này đã có lô',
};
export const ingestMessage = (code: IngestErrorCode, address: string): string =>
  INGEST_MESSAGES[code].replace('{address}', address);
/** `INGEST_UNSUPPORTED` for a CSV that is not UTF-8 (§7.1) and a PDF with a password (P13). */
export const INGEST_CSV_NOT_UTF8 = 'File CSV cần lưu dạng UTF-8';
export const INGEST_PDF_PASSWORD = 'PDF có mật khẩu';
/** `INGEST_TOO_LARGE` names the cap it hit (§14 "nói rõ trần"); `rows` also names the sheet's count. */
export function tooLargeMessage(cap: 'pages' | 'images' | 'rows', count?: number): string {
  switch (cap) {
    case 'pages': return `Tài liệu dài quá ${INGEST_LIMITS.pages} trang`;
    case 'images': return `Mỗi lần gửi tối đa ${INGEST_LIMITS.images} ảnh`;
    case 'rows': return `Bảng có ${count ?? 'hơn ' + INGEST_LIMITS.rows} dòng, mỗi lô tối đa ${INGEST_LIMITS.rows} dòng`;
  }
}
/**
 * `INGEST_TOO_LARGE` when a file is past a size cap of the upload check or a reader (D84): the 10 000-row sheet cap,
 * the Office element caps, the 24 MiB inflate budget. Unlike the 500-row batch cap (a split offer by row range,
 * D61), nothing can be read, so the person is asked to split the file itself.
 */
export const INGEST_SPLIT_FILE = 'File quá lớn để em đọc một lần, hãy chia thành các file nhỏ hơn rồi gửi lại';
/** `INGEST_SCAN_UNSUPPORTED` as a notice (D64): "Trang 4, 5 là ảnh scan, …". */
export const scanPagesMessage = (pages: readonly number[]): string =>
  `Trang ${pages.join(', ')} là ảnh scan, model hiện tại không đọc được`;

/**
 * How one sheet's columns become rows (§7.4): column letters → field names of `kind`. `header` is the header row's
 * own words by column (the server reads them from the sheet, at most `MAPPING_HEADER_MAX` columns), so the mapping
 * bar can list every column, a skipped one too (§8.2).
 */
export type IngestSheetMapping = { sheet: string; kind: BatchRowKind; headerRow: number; columns: Record<string, string>;
  header?: Record<string, string> };
/** The header columns a mapping keeps (T4.2). */
export const MAPPING_HEADER_MAX = 200;
export type IngestMapping = { sheets: IngestSheetMapping[] };
/**
 * The default project, finance categories and parent card of what a batch writes. A statement has both directions,
 * so there is one default category per direction (§7.4 step 4 "danh mục mặc định chi và thu").
 */
export type IngestDefaults = { projectId?: Id; categoryOutId?: Id; categoryInId?: Id; parentItemId?: Id };
/** 1-based data rows, both ends included (the API stores the half-open `[from, to + 1)`). */
export type IngestRowRange = { from: number; to: number };
/**
 * A non-fatal note on a job that still reaches `ready` (D52): scan pages the model could not be sent (D64, D86), the
 * visible sheets past the 5-sheet cap (D85), the rows past the 500-row cap left out (D96).
 */
export type IngestNotice =
  | { code: 'INGEST_SCAN_UNSUPPORTED'; pages: number[] }
  | { code: 'INGEST_SHEETS_SKIPPED'; sheets: string[] }
  | { code: 'INGEST_ROWS_CAPPED'; kept: number }
  /** D107: a job of a split sheet — the sheet's data rows, and the rows this job reads (`to` within them). */
  | { code: 'INGEST_SPLIT'; rowCount: number; from: number; to: number };
/** D85: "em chỉ đọc 5 sheet đầu", naming the sheets left out. */
export const sheetsSkippedMessage = (sheets: readonly string[]): string =>
  `Em chỉ đọc 5 sheet đầu, chưa đọc: ${sheets.join(', ')}`;
/** D96: a job keeps its first `kept` rows; `address` is how Hana addresses the person. */
export const rowsCappedMessage = (kept: number, address: string): string =>
  `Em chỉ giữ ${kept} dòng đầu; phần còn lại ${address} gửi riêng nhé`;
export function noticeMessage(n: IngestNotice, address: string): string {
  switch (n.code) {
    case 'INGEST_SCAN_UNSUPPORTED': return scanPagesMessage(n.pages);
    case 'INGEST_SHEETS_SKIPPED': return sheetsSkippedMessage(n.sheets);
    case 'INGEST_ROWS_CAPPED': return rowsCappedMessage(n.kept, address);
    case 'INGEST_SPLIT': return `Bảng có ${n.rowCount} dòng; lô này gồm dòng ${n.from}–${n.to}.`;
  }
}

/** Every field a row may carry, as the card names it (§8.1, P3); Kiểm's sentences name fields by it too. A unit test
 *  holds it to the extract schema's `FIELD_NAMES`. */
export const INGEST_FIELD_LABELS: Readonly<Record<string, string>> = {
  title: 'Tiêu đề', description: 'Mô tả', deadline: 'Hạn hoàn thành', priority: 'Mức ưu tiên', assigneeId: 'Người nhận',
  kind: 'Loại', name: 'Tên', labels: 'Nhãn', taxCode: 'Mã số thuế', phone: 'Điện thoại', email: 'Email',
  address: 'Địa chỉ', website: 'Website', note: 'Ghi chú',
  type: 'Loại bên liên quan', power: 'Ảnh hưởng', interest: 'Quan tâm', engagementCurrent: 'Gắn kết hiện tại',
  engagementDesired: 'Gắn kết mong muốn', strategyNote: 'Ghi chú chiến lược', projectRole: 'Vai trò trong dự án',
  contractNo: 'Số hợp đồng', value: 'Giá trị', startDate: 'Ngày bắt đầu', endDate: 'Ngày kết thúc',
  status: 'Trạng thái',
  plannedAmount: 'Số tiền kế hoạch', plannedDate: 'Ngày dự kiến', actualAmount: 'Số tiền thực tế',
  actualDate: 'Ngày thực tế',
  current: 'Giá trị hiện tại', month: 'Tháng', progress: 'Tiến độ', issues: 'Vướng mắc', nextActions: 'Việc tiếp theo',
};

/**
 * The §8.2 filters (P4, P5): Tạo mới = an `ok` create, Cập nhật = an `ok` update, Nghi trùng = `duplicate`,
 * Cần sửa = `needs_fix`, Không đưa vào = `rejected` only (D111).
 */
export const BATCH_FILTER_KEYS = ['all', 'create', 'update', 'duplicate', 'needs_fix', 'rejected'] as const;
export type BatchFilterKey = (typeof BATCH_FILTER_KEYS)[number];
/** A job's counts (P4): by kind (rejected rows left out, as the card head counts what it shows above them), by
 *  filter, the rows selected ("Duyệt N") and the finance rows among them ("Gồm K khoản tài chính"). */
export type IngestJobCounts = { byKind: Partial<Record<BatchRowKind, number>>; byFilter: Record<BatchFilterKey, number>;
  included: number; includedFinance: number };

export type IngestDocumentDTO = { id: Id; filename: string; mediaType: string; size: number; pageCount: number | null };
export type IngestStageDTO = { unit: number; units: number; label: string };
export type IngestJobDTO = {
  id: Id; conversationId: Id | null; sourceKind: IngestSourceKind; state: IngestJobState;
  stage: IngestStageDTO | null; unitsDone: number; unitsTotal: number;
  documents: IngestDocumentDTO[]; anchorItemId: Id | null;
  mapping: IngestMapping | null; defaults: IngestDefaults; rowRange: IngestRowRange | null;
  held: boolean; estimateTokens: number | null;
  /** D126: what is left of the person's daily token quota while the job waits on "Chạy" (`held`); null with no
   *  quota, or when the job is not held. */
  quotaLeft: number | null;
  /** P4: the tokens the job's reading used, input and output. */
  tokensUsed: number; counts: IngestJobCounts;
  errorCode: IngestErrorCode | 'ASSISTANT_QUOTA' | null; errorMessage: string | null; notices: IngestNotice[];
  dropped: number; rowCount: number; includedCount: number;
  /** D107: the next 500-row window of a split sheet, after `rowRange.to`, while the sheet has rows past it and no live
   *  job of the same document already holds any of them; else null. */
  nextRange: [number, number] | null;
  /** D113: on the pending list only, the update rows whose record moved, went or is out of the person's sight since
   *  Hana read it (counted, never named; the list writes nothing). Null on a single job's read: its rows carry their
   *  own `DRIFTED` marks. */
  drifted: number | null;
  readyAt: string | null; expiresAt: string | null; appliedAt: string | null;
  /** D102: the one action that wrote the batch, whether it still stands or was undone, and the end of its undo
   *  window. The job stays `applied` after an undo; `actionState` is what tells the card "Đã hoàn tác". */
  actionId: Id | null; actionState: IngestActionState | null; undoExpiresAt: string | null;
  /** AI S6 §9: the outside system a batch made by `ingest.from_external` read its file from (the server's name then)
   *  and the file's name; null for an upload or a paste. The resource's URI and storage key stay on the server. */
  externalOrigin: IngestExternalOrigin | null;
  createdAt: string; version: number;
};
export type IngestExternalOrigin = { serverName: string; filename: string };
/** D102: the state of an applied batch's action (`assistant_actions.status`). */
export const INGEST_ACTION_STATES = ['executed', 'undone'] as const;
export type IngestActionState = (typeof INGEST_ACTION_STATES)[number];
/** Where in the original a row was read (§5.1). */
export type BatchRowSource = { inputOrd: number; page?: number; sheet?: string; row?: number; cell?: string; quote?: string };
/** A row's parent: an existing card, or the `seq` of another task row of the batch. */
export type BatchParentRef = { itemId: Id } | { seq: number };
/** The references Đối chiếu resolves for a row (§9) that the card names and the person may pick (P2). */
export const BATCH_REF_KEYS = ['projectId', 'partnerId', 'contactId', 'stakeholderId', 'categoryId', 'objectiveId'] as const;
export type BatchRefKey = (typeof BATCH_REF_KEYS)[number];
/** `name`: a finance row's project alone, without its code (D136), for the group heading. */
export type BatchRowLabel = { label: string; name?: string };
/** An update row's field: the record's value now (struck through on the card) and the row's. */
export type BatchRowChange = { field: string; label: string; before: unknown; after: unknown };
/**
 * What the card draws for a row (P3, D109), read as the person sees the company now: a hidden record gives no label
 * (the key is absent). `refs` adds `priorityId` for a metric or review under a priority. `changes` only on an update
 * whose target the person may read; `duplicateOf` only on a duplicate (D121).
 */
export type BatchRowView = {
  title: string; target?: BatchRowLabel; parent?: BatchRowLabel;
  refs: Partial<Record<BatchRefKey | 'priorityId', BatchRowLabel>>;
  changes?: BatchRowChange[]; duplicateOf?: BatchRowLabel;
};
export type BatchRowDTO = {
  id: Id; seq: number; kind: BatchRowKind; op: BatchRowOp; targetId: Id | null; targetVersion: number | null;
  parentRef: BatchParentRef | null; fields: Record<string, unknown>; source: BatchRowSource; status: BatchRowStatus;
  included: boolean; reasons: string[]; edited: boolean; filled: string[]; version: number; view: BatchRowView;
};
/** The record types a batch writes and `record_sources` names (024). */
export const RECORD_SOURCE_TYPES = ['task', 'note', 'partner', 'contact', 'stakeholder', 'participant', 'contract',
  'finance_entry', 'metric', 'review'] as const;
export type RecordSourceType = (typeof RECORD_SOURCE_TYPES)[number];
/** `POST /api/assistant/sources/resolve` (D115): at most 50 records a call. */
export const SOURCES_RESOLVE_MAX = 50;
export type RecordSourcesBody = { refs: { type: RecordSourceType; id: Id }[] };
/**
 * One document backing a record (D115, D79). `filename`, `mediaType` (D143: how the chip opens it) and `source` (the
 * page, sheet or row it was read from) only when the caller may open the document; else `filename: null`,
 * `mediaType: null`, `source: {}`, `canOpen: false` ("Tài liệu nguồn · không có quyền mở").
 */
export type RecordSourceDTO = { documentId: Id; filename: string | null; mediaType: string | null;
  source: { page?: number; sheet?: string; row?: number }; canOpen: boolean };
/** Only the records the caller sees and that have a source; a hidden one has no entry, as a missing one. */
export type RecordSourcesDTO = { items: { type: RecordSourceType; id: Id; sources: RecordSourceDTO[] }[] };

export type BatchDetailDTO = { job: IngestJobDTO; rows: Page<BatchRowDTO> };
/** `PATCH …/rows/:rowId` → the row as Kiểm left it, and the batch children D93 deselected with it. */
export type BatchRowPatchedDTO = { row: BatchRowDTO; deselected: Id[] };
/** `POST /api/assistant/ingest` → 202. */
export type IngestCreatedDTO = { jobId: Id; conversationId: Id };
/** A row `BATCH_CHANGED` names (P19), in the refusal's `rows`. */
export type BatchChangedRow = { id: Id; reasons: string[] };
/** A card the batch wrote (L4 D153): what the Map slides in and glows. */
export type BatchAppliedObject = { type: 'task' | 'note'; id: Id };
/** At most this many cards in `objects`; a bigger batch glows its first ones. */
export const BATCH_APPLIED_OBJECTS_MAX = 200;
/** `POST …/batches/:id/apply` → the job, now `applied`, the one action that undoes the whole batch (§8.5), and the
 *  cards it wrote, created or updated, in write order (parents first), finance and other records left out (D153). */
export type BatchAppliedDTO = { job: IngestJobDTO; actionId: Id; undoExpiresAt: string | null;
  objects: BatchAppliedObject[] };

/** D107: `documentId` names a sheet one of the person's own jobs read, for the next range of a split. */
export type IngestCreateBody = { conversationId?: Id; uploadSessionIds?: Id[]; documentId?: Id; text?: string;
  anchorItemId?: Id; rowRange?: IngestRowRange; mapping?: IngestMapping };
/**
 * P2: the references the person picks for a row (a project, partner, contact, stakeholder, category or objective,
 * each from `…/options`); null clears a pick. Kept on the row as code fields; Đối chiếu takes a pick over the hints,
 * only among what the person sees, and a pick it does not find is the same 422 whether hidden or missing.
 */
export type BatchRowRefs = Partial<Record<BatchRefKey, Id | null>>;
export type BatchRowPatchBody = { version: number; included?: boolean; fields?: Record<string, unknown>;
  targetId?: Id | null; op?: BatchRowOp; refs?: BatchRowRefs };
/** `GET …/rows/:rowId/options?for=…&q=`: what a row may point at (P2), at most `BATCH_OPTIONS_MAX`, visible only. */
export const BATCH_OPTION_FOR = ['target', 'project', 'partner', 'contact', 'stakeholder', 'category', 'objective'] as const;
export type BatchOptionFor = (typeof BATCH_OPTION_FOR)[number];
export const BATCH_OPTIONS_MAX = 20;
export type BatchRowOption = { id: Id; label: string; hint: string | null };
export type BatchRowOptionsDTO = { items: BatchRowOption[] };
/**
 * D139: `GET …/batches/:id/options?for=project|category[&direction=in|out]&q=` — the batch's own defaults (the mapping
 * bar), no row needed; the same sources, sight rules and limit as a row's options, a category within `direction`.
 */
export const BATCH_JOB_OPTION_FOR = ['project', 'category'] as const;
export type BatchJobOptionFor = (typeof BATCH_JOB_OPTION_FOR)[number];
export type BatchRowsFilter = { status?: BatchRowStatus[]; kind?: BatchRowKind[]; op?: BatchRowOp[] };
export type BatchRowsBody = { version: number; filter: BatchRowsFilter; included: boolean };
export type BatchMappingBody = { version: number; mapping: IngestMapping; defaults: IngestDefaults };
/** L4 D152: `PUT …/parent` — the card an unanchored batch's top task/note rows go under ("Đặt dưới"), or none
 *  ("Việc lẻ"); stored as `defaults.parentItemId`. */
export type BatchParentBody = { version: number; parentItemId: Id | null };
/** `run`, `cancel`, `discard`, `retry` and `apply`. */
export type BatchVersionBody = { version: number };
/** `GET/PUT /api/admin/ai/ingest` (§11.3): the two switches under their own `ingest_version`; `providerName` names
 *  where the documents go, the company default model's provider, null when there is no usable default. */
export type IngestSettingsDTO = { ingestEnabled: boolean; ingestFinanceEnabled: boolean; version: number;
  providerName: string | null };
export type IngestSettingsBody = { version: number; ingestEnabled: boolean; ingestFinanceEnabled: boolean };
